import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Check, Loader2, Sparkles, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/6ad1a731-3032-4010-8a9c-d4467dd36bd6/images/d5715ba9fc9a5b6c35a43b102303fb4d6c2cf10758fab01fc88fb36771d3c972.png";

export default function Hero({ registrationCount, onRegistered }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Por favor, completa todos los campos.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/registrations`, {
        name: name.trim(),
        email: email.trim(),
      });
      setSuccess(true);
      toast.success("¡Muchas gracias! Te avisaremos cuando esté el libro.");
      onRegistered?.();
      setName("");
      setEmail("");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "No pudimos registrarte. Inténtalo de nuevo.";
      toast.error(typeof msg === "string" ? msg : "Error en el registro");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center pt-28 pb-20 overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_BG}
          alt="Tiburón nadando en el océano profundo"
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/70 to-slate-950" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-transparent to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center w-full">
        {/* Left: Headline */}
        <div className="lg:col-span-7 animate-fade-up">
          <div className="mb-10 sm:mb-12 [perspective:1200px]">
            <a
              href="#register"
              data-testid="hero-book-image"
              className="relative inline-block group animate-float"
              style={{ transformStyle: "preserve-3d" }}
              aria-label="Reserva tu libro Sin aletas no hay paraíso"
            >
              <div className="absolute -inset-10 rounded-3xl bg-gradient-to-br from-cyan-400/40 via-cyan-300/15 to-rose-500/30 blur-3xl opacity-70 group-hover:opacity-100 transition-opacity" />
              <img
                src="https://customer-assets.emergentagent.com/job_crowdfund-ocean/artifacts/90hc15ha_WhatsApp%20Image%202026-05-20%20at%2014.16.41%20%281%29%20-%20Editada.png"
                alt="Libro 'Sin aletas no hay paraíso' por Natalia Rodríguez"
                className="relative h-56 sm:h-64 lg:h-72 w-auto object-contain transition-transform duration-700 group-hover:[transform:rotateY(-8deg)_rotateX(2deg)_scale(1.03)]"
                style={{
                  transform: "rotateY(-18deg) rotateX(6deg)",
                  filter:
                    "drop-shadow(0 30px 35px rgba(0,0,0,0.6)) drop-shadow(0 10px 15px rgba(0,0,0,0.45))",
                }}
              />
            </a>
          </div>

          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-400/30 mb-7"
          >
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs uppercase tracking-[0.2em] font-medium text-cyan-300">
              Pre-lanzamiento · Crowdfunding 2026
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight mb-7"
          >
            Sin aletas
            <br />
            <span className="text-gradient">no hay paraíso.</span>
          </h1>

          <p className="font-body text-lg sm:text-xl text-slate-300 leading-relaxed max-w-xl mb-10">
            Cada año, más de <span className="text-cyan-300 font-semibold">100 millones</span> de
            tiburones mueren por la sobrepesca o por prácticas como el <span className="text-rose-300 font-semibold">finning</span>,
            que consiste en cortarles las aletas y devolverlos al mar vivos, condenados a una muerte segura.{" "}
            <span className="text-white font-semibold">"Sin aletas no hay paraíso"</span> es un libro ilustrado
            que alerta sobre la desaparición de los tiburones y las consecuencias para todos.
          </p>

          <div className="flex items-center gap-6 text-sm text-slate-400">
            <div className="flex -space-x-2">
              {[
                "from-cyan-400 to-cyan-600",
                "from-rose-400 to-rose-600",
                "from-indigo-400 to-indigo-600",
              ].map((g, i) => (
                <div
                  key={i}
                  className={`w-9 h-9 rounded-full bg-gradient-to-br ${g} border-2 border-slate-950`}
                />
              ))}
            </div>
            <div>
              <span className="text-cyan-300 font-semibold">
                {registrationCount > 0
                  ? `${registrationCount} ${registrationCount === 1 ? "amigo" : "amigos"} de los tiburones`
                  : "Sé el primer amigo de los tiburones"}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Form Card */}
        <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <div
            id="register"
            data-testid="registration-card"
            className="relative bg-slate-950/70 backdrop-blur-xl border border-white/10 rounded-3xl p-8 sm:p-10 shadow-2xl"
          >
            <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-cyan-400/30 via-transparent to-rose-500/20 -z-10 blur-xl" />

            {!success ? (
              <>
                <div className="mb-7">
                  <span className="text-xs uppercase tracking-[0.2em] font-medium text-cyan-400">
                    No te quedes sin tu libro
                  </span>
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-3 leading-tight">
                    Te avisamos cuando lancemos nuestro crowdfunding.
                  </h2>
                </div>

                <form
                  data-testid="registration-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-xs uppercase tracking-[0.15em] font-medium text-slate-400 mb-2"
                    >
                      Nombre
                    </label>
                    <input
                      id="name"
                      type="text"
                      data-testid="name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Tu nombre"
                      required
                      disabled={loading}
                      className="w-full bg-slate-900/60 border border-slate-700 focus:border-cyan-400 rounded-full px-6 py-4 text-white outline-none transition-all placeholder:text-slate-500 disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs uppercase tracking-[0.15em] font-medium text-slate-400 mb-2"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      type="email"
                      data-testid="email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@correo.com"
                      required
                      disabled={loading}
                      className="w-full bg-slate-900/60 border border-slate-700 focus:border-cyan-400 rounded-full px-6 py-4 text-white outline-none transition-all placeholder:text-slate-500 disabled:opacity-50"
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="submit-registration-button"
                    disabled={loading}
                    className="w-full mt-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold px-6 py-4 rounded-full transition-all duration-300 shadow-[0_0_20px_rgba(34,211,238,0.3)] hover:shadow-[0_0_30px_rgba(34,211,238,0.5)] hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Registrando…
                      </>
                    ) : (
                      <>
                        Quiero que me avises
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>

                <p className="text-xs text-slate-500 mt-5 text-center">
                  Solo usaremos tu correo para avisarte del lanzamiento. Sin spam.
                </p>
              </>
            ) : (
              <div
                data-testid="registration-success"
                className="text-center py-2 animate-fade-up"
              >
                <div className="w-16 h-16 rounded-full bg-cyan-500/15 border border-cyan-400/40 flex items-center justify-center mx-auto mb-5">
                  <Check className="w-8 h-8 text-cyan-400" strokeWidth={3} />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-3">
                  ¡Bienvenido/a amigo/a de los tiburones!
                </h3>
                <p className="text-slate-300 mb-6">
                  Muchas gracias: ¡te avisaremos cuando esté el libro! Mientras tanto, ayúdanos a
                  llegar más lejos:
                  <span className="block text-cyan-300 font-semibold mt-1">
                    Invita a un amigo a unirse.
                  </span>
                </p>

                <ShareBlock />

                <button
                  onClick={() => setSuccess(false)}
                  data-testid="register-another-button"
                  className="mt-6 text-slate-400 hover:text-cyan-300 text-xs font-medium underline-offset-4 hover:underline"
                >
                  Registrar a otra persona
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function ShareBlock() {
  const shareUrl = window.location.origin;
  const shareText =
    "Acabo de unirme a 'Sin aletas no hay paraíso', una campaña de crowdfunding para proteger los tiburones y el océano. ¿Te unes?";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success("¡Enlace copiado! Compártelo donde quieras.");
    } catch {
      toast.error("No se pudo copiar el enlace.");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Sin aletas no hay paraíso",
          text: shareText,
          url: shareUrl,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copy();
    }
  };

  const whatsapp = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
  const twitter = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    shareText
  )}&url=${encodeURIComponent(shareUrl)}`;
  const telegram = `https://t.me/share/url?url=${encodeURIComponent(
    shareUrl
  )}&text=${encodeURIComponent(shareText)}`;

  return (
    <div data-testid="share-block" className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <a
          href={whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-whatsapp"
          className="flex items-center justify-center gap-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 hover:text-emerald-200 rounded-full px-3 py-2.5 text-xs font-semibold transition-all"
        >
          WhatsApp
        </a>
        <a
          href={twitter}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-twitter"
          className="flex items-center justify-center gap-2 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-400/30 text-sky-300 hover:text-sky-200 rounded-full px-3 py-2.5 text-xs font-semibold transition-all"
        >
          X / Twitter
        </a>
        <a
          href={telegram}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-telegram"
          className="flex items-center justify-center gap-2 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 hover:text-indigo-200 rounded-full px-3 py-2.5 text-xs font-semibold transition-all"
        >
          Telegram
        </a>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={copy}
          data-testid="share-copy"
          className="flex items-center justify-center gap-2 bg-slate-800/60 hover:bg-slate-800 border border-white/10 text-slate-200 rounded-full px-3 py-2.5 text-xs font-semibold transition-all"
        >
          <Copy className="w-3.5 h-3.5" />
          Copiar enlace
        </button>
        <button
          onClick={nativeShare}
          data-testid="share-native"
          className="flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 rounded-full px-3 py-2.5 text-xs font-bold transition-all"
        >
          <Share2 className="w-3.5 h-3.5" />
          Compartir
        </button>
      </div>
    </div>
  );
}
